function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { ChevronDown, Clear } from "@bolteu/kalep-react-icons";
import { MultiSelectChip } from "../_internal/MultiSelectChip";
import { cx } from "../../helpers/utils/cx";
import { noop, noopNull } from "../../helpers/utils/noop";
import { Badge, GhostButton, SpinnerIcon } from "../..";
import { DEFAULT_LOADING_SPINNER_ARIA_LABEL, DEFAULT_TOGGLE_BUTTON_ARIA_LABEL } from "./BaseComboBox";
const CHIPS_COUNT_SAFEAREA = 24;
const MIN_INPUT_WIDTH = 6 * 16;
const sizeStyles = {
  sm: "min-h-9 pl-3 pr-2 bolt-font-body-s-regular gap-2",
  md: "min-h-12 pl-3 pr-2 bolt-font-body-m-regular gap-2",
  lg: "min-h-14 pl-3 pr-2 bolt-font-body-m-regular gap-2"
};
const wrapperStyles = {
  idle: "border-special-nulled focus:border-action-primary text-primary",
  disabled: "border-neutral-secondary cursor-not-allowed text-tertiary bg-special-nulled placeholder-tertiary",
  error: "border-danger-primary text-primary"
};
const getInputTextStyles = (disabled, hasValue) => {
  if (disabled) {
    return "text-tertiary placeholder-tertiary";
  }
  if (hasValue) {
    return "text-primary placeholder-secondary";
  }
  return "text-secondary placeholder-secondary";
};
function calculateChipWidths(inputWrapperRef, chipRefs, size, chipGap, value) {
  // Total external width of the component.
  // ref.current not being null is checked before calling this function.
  const wrapperWidth = inputWrapperRef.current.getBoundingClientRect().width;

  // Calculate space that we have available for representing current state and search input.
  // (TOTAL - (START_PADDING + END_PADDING) - GAP_BEFORE_CHEVRON - CHEVRON_WIDTH_SIZE_SPECIFIC)
  const availableWidth = wrapperWidth - 20 - 8 - (size === "sm" ? 28 : 32);

  // Let's drop the large MIN_INPUT_WIDTH for now
  const IDLE_MIN_INPUT_WIDTH = 2 * 16;
  const COUNTER_BADGE_WIDTH = 24;

  // Early return values for when we have not selected anything.
  if (value.length === 0) {
    return {
      maxIndexToShow: 0,
      chipsVisibleWidth: 0,
      transformWhenOpen: 0,
      chipsFullWidth: 0,
      closedInputWidth: availableWidth,
      openInputWidth: availableWidth,
      showBadgeOnly: false
    };
  }

  // Early return values for when the field is too small to show chips, so we show a Badge instead.
  if (wrapperWidth < 161 && value.length) {
    return {
      maxIndexToShow: 0,
      chipsVisibleWidth: COUNTER_BADGE_WIDTH,
      transformWhenOpen: 0,
      chipsFullWidth: COUNTER_BADGE_WIDTH,
      closedInputWidth: availableWidth - COUNTER_BADGE_WIDTH,
      openInputWidth: availableWidth - COUNTER_BADGE_WIDTH,
      showBadgeOnly: true
    };
  }

  // Otherwise (enough space, got selected items), calculate widths for chips and input.

  // safeAreaWidth is the space we cannot use for showing chips inside availableWidth.
  // If there's only one chip don't account for the hidden chip count area
  const safeAreaWidth = value.length < 2 ? chipGap + IDLE_MIN_INPUT_WIDTH : CHIPS_COUNT_SAFEAREA + chipGap * 2 + IDLE_MIN_INPUT_WIDTH;
  let chipsFullWidth = 0;
  let chipsVisibleWidth = 0;
  let chipsOverflow = false;
  let maxIndexToShow = 0;
  for (let i = 0; i < value.length; i++) {
    const option = value[i];
    if (option.value && chipRefs.current[option.value]) {
      const chipElement = chipRefs.current[option.value].current;
      if (chipElement) {
        let chipWidth = chipElement.getBoundingClientRect().width;

        // If the chip in it's natural width won't fit calculate the max width it can have and truncate.
        // it's possible that this chip won't fit and is skipped in the next step, so the style setting here might be inefficient, depending if it triggers reflow right away.
        if (chipWidth + safeAreaWidth >= availableWidth) {
          chipWidth = Math.max(availableWidth - safeAreaWidth, 0); // rather aggressive and can result in *very* tiny chips.
          chipElement.style.width = chipWidth === 0 ? "auto" : `${chipWidth}px`;
          chipElement.style.overflow = "hidden";
          chipElement.style.textOverflow = "ellipsis";
        }

        // Apply gap, if it's not the first chip.
        const gap = i === 0 ? 0 : chipGap;
        chipsFullWidth += gap + chipWidth;

        // While chips are not overflowing, add them to the visible width,
        // if we've detected overflow starting, mark it and skip this part for the remaining chips.
        if (!chipsOverflow) {
          if (chipsFullWidth + safeAreaWidth <= availableWidth) {
            chipsVisibleWidth += gap + chipWidth;
            maxIndexToShow = i;
          } else {
            chipsOverflow = true;
          }
        }
      }
    }
  }
  const closedInputWidth = availableWidth - chipsVisibleWidth - (chipsOverflow ? CHIPS_COUNT_SAFEAREA + chipGap : 0);
  const openInputWidth = Math.max(availableWidth - chipsFullWidth - chipGap, MIN_INPUT_WIDTH);
  const transformWhenOpen = Math.min(availableWidth - chipsFullWidth - openInputWidth, 0);
  return {
    maxIndexToShow,
    chipsVisibleWidth,
    transformWhenOpen,
    chipsFullWidth,
    closedInputWidth,
    openInputWidth,
    showBadgeOnly: false
  };
}
export function ComboBoxTriggerMulti(props) {
  const {
    clearable,
    closeMenu,
    disabled,
    error,
    id,
    isOpen,
    generatedId,
    getDropdownProps,
    getInputProps,
    getOptionLabel,
    getToggleButtonProps,
    inputValue,
    inputWrapperRef,
    loading,
    loadingSpinnerAriaLabel = DEFAULT_LOADING_SPINNER_ARIA_LABEL,
    name,
    noResultsMessageId,
    onClear,
    onDelete,
    onInputChange,
    openMenu,
    placeholder,
    required,
    renderStartSlot,
    shouldShowNoResultsMessage,
    shouldRenderEndSlotIcon = true,
    size = "md",
    toggleButtonAriaLabel = DEFAULT_TOGGLE_BUTTON_ARIA_LABEL,
    toggleButtonRef,
    value
  } = props;
  const inputRef = React.useRef(null);
  const chipRefs = React.useRef({});
  const isValueArray = Array.isArray(value);
  const [maxChipIndex, setMaxChipIndex] = React.useState(isValueArray ? value.length - 1 : -1);
  const [visibleChipOffset, setVisibleChipOffset] = React.useState(0);
  const [chipTransform, setChipTransform] = React.useState(0);
  const [showBadgeForChips, setShowBadgeForChips] = React.useState(false);
  const [chipFullWidth, setChipFullWidth] = React.useState(0);
  const [inputWidthWhenClosed, setInputWidthWhenClosed] = React.useState(MIN_INPUT_WIDTH);
  const [inputWidthWhenOpen, setInputWidthWhenOpen] = React.useState(MIN_INPUT_WIDTH);
  const hiddenChipCount = isValueArray ? value.length - maxChipIndex - 1 : 0;
  const chipGap = React.useMemo(() => size === "sm" ? 4 : 6, [size]);
  const loadingSpinnerId = id ?? `kalep-combo-box-loading-spinner-${generatedId}`;
  const hasValue = isValueArray ? Boolean(value.length) : Boolean(value);
  const hasMoreThanOneChip = isValueArray && value.length > 1;
  if (isValueArray) {
    value.forEach(option => {
      if (option.value && !chipRefs.current[option.value]) {
        chipRefs.current[option.value] = chipRefs.current[option.value] || /*#__PURE__*/React.createRef();
      }
    });
  }
  const ddProps = getDropdownProps?.(inputRef);
  const inputA11yProps = getInputProps(_objectSpread(_objectSpread({
    ref: inputRef,
    disabled,
    onChange: onInputChange
  }, ddProps), {}, {
    onKeyDown: e => {
      ddProps?.onKeyDown?.(e);
      if (inputValue.length === 0 && e.key === "Backspace" && isValueArray && value.length > 0) {
        onDelete?.(value.length - 1); // Remove last selected item
      }
      if (e.key === "Escape") {
        inputRef.current?.blur();
      }
    }
  }));
  if (loading) {
    inputA11yProps["aria-labelledby"] = `${inputA11yProps["aria-labelledby"]} ${loadingSpinnerId}`;
  }
  if (shouldShowNoResultsMessage) {
    inputA11yProps["aria-labelledby"] = `${inputA11yProps["aria-labelledby"]} ${noResultsMessageId}`;
  }
  const handleClearButtonClick = React.useCallback(e => {
    onClear?.();
    e.preventDefault();
    e.stopPropagation();
  }, [onClear]);
  const onClick = React.useCallback(e => {
    if (disabled) {
      return;
    }
    if (!toggleButtonRef?.current?.contains(e.target)) {
      openMenu();
    }
    if (toggleButtonRef?.current?.contains(e.target) && isOpen) {
      closeMenu();
    }
  }, [openMenu, closeMenu, isOpen, toggleButtonRef, disabled]);
  const renderEndSlot = React.useCallback(() => {
    if (props.renderEndSlot) {
      return /*#__PURE__*/React.createElement("div", {
        className: cx("shrink-0 flex items-center max-w-6 gap-1")
      }, props.renderEndSlot(props, noopNull));
    }
    const showClearButton = !disabled && (hasValue || !!inputValue) && clearable && hasValue && hasMoreThanOneChip;
    return /*#__PURE__*/React.createElement("div", {
      className: cx("flex items-center justify-end flex-shrink-0", size !== "sm" && "gap-1")
    }, showClearButton && /*#__PURE__*/React.createElement(GhostButton, {
      onClick: handleClearButtonClick,
      overrideClassName: cx("group/clear", "w-0 flex-shrink-0 z-[60]", "group-hover/trigger:w-6 group-hover/trigger:mx-1"),
      tabIndex: -1
    }, /*#__PURE__*/React.createElement("div", {
      className: cx(isOpen ? "flex" : "hidden", "group-hover/trigger:flex", "justify-center items-center")
    }, /*#__PURE__*/React.createElement(Clear, {
      "aria-hidden": "true",
      className: cx("text-tertiary group-hover/clear:text-secondary"),
      size: size === "sm" ? "md" : "lg"
    }))), loading && /*#__PURE__*/React.createElement(SpinnerIcon, {
      id: loadingSpinnerId,
      "aria-label": loadingSpinnerAriaLabel,
      className: cx("flex-none mx-1 animate-spin text-action-secondary"),
      size: size,
      role: "status"
    }), !loading && shouldRenderEndSlotIcon && /*#__PURE__*/React.createElement("button", _extends({
      type: "button"
    }, getToggleButtonProps({
      ref: toggleButtonRef,
      "aria-label": toggleButtonAriaLabel
    }), {
      className: cx("border-0 bg-transparent cursor-pointer m-0 p-0 flex items-center", disabled && "cursor-not-allowed"),
      disabled: disabled
    }), /*#__PURE__*/React.createElement(ChevronDown, {
      className: cx("flex-none transition-transform m-1", disabled ? "text-tertiary" : "text-primary", isOpen && "rotate-180"),
      size: size === "sm" ? "md" : "lg"
    })));
  }, [props, disabled, hasValue, inputValue, clearable, hasMoreThanOneChip, size, handleClearButtonClick, isOpen, loading, loadingSpinnerId, loadingSpinnerAriaLabel, shouldRenderEndSlotIcon, getToggleButtonProps, toggleButtonRef, toggleButtonAriaLabel]);
  React.useLayoutEffect(() => {
    if (isValueArray && inputWrapperRef.current) {
      const {
        maxIndexToShow,
        chipsVisibleWidth,
        transformWhenOpen,
        chipsFullWidth,
        closedInputWidth,
        openInputWidth,
        showBadgeOnly
      } = calculateChipWidths(inputWrapperRef, chipRefs, size, chipGap, value);
      setMaxChipIndex(maxIndexToShow);
      setVisibleChipOffset(chipsVisibleWidth);
      setChipTransform(transformWhenOpen);
      setChipFullWidth(chipsFullWidth);
      setInputWidthWhenClosed(closedInputWidth);
      setInputWidthWhenOpen(openInputWidth);
      setShowBadgeForChips(showBadgeOnly);
    }
  }, [value, isValueArray, isOpen, chipGap, inputWrapperRef, size]);
  return /*#__PURE__*/React.createElement("div", {
    ref: inputWrapperRef,
    className: cx("group/trigger", "flex justify-end items-center w-full", "border-2 border-solid rounded-md", "outline-none overflow-hidden", "bg-neutral-secondary caret-border-action-primary", "ease-linear duration-100", disabled ? "cursor-not-allowed" : "cursor-text", disabled && wrapperStyles.disabled, error && wrapperStyles.error, !disabled && !error && wrapperStyles.idle, sizeStyles[size], !error && isOpen && "border-action-primary bg-special-nulled"),
    onClick: onClick,
    onKeyPress: noop,
    role: "textbox",
    tabIndex: -1
  }, typeof renderStartSlot === "function" && /*#__PURE__*/React.createElement("div", {
    className: cx("flex items-center max-w-6")
  }, renderStartSlot(props, noopNull), " "), /*#__PURE__*/React.createElement("div", {
    className: cx("w-full min-w-0", typeof renderStartSlot === "function" && "overflow-hidden")
  }, /*#__PURE__*/React.createElement("div", {
    className: cx("relative w-full flex flex-1 items-center transition-transform", "min-w-0",
    // Preventing overflowing flex container - https://stackoverflow.com/questions/36230944/prevent-flex-items-from-overflowing-a-container
    size === "sm" ? "h-6" : "h-7", isOpen ? "overflow-visible" : "overflow-hidden"),
    style: {
      transform: `translateX(${isOpen ? chipTransform : 0}px)`,
      gap: chipGap
    }
  }, hasValue && Array.isArray(value) ? /*#__PURE__*/React.createElement(React.Fragment, null, showBadgeForChips && /*#__PURE__*/React.createElement(Badge, {
    badgeContent: value.length,
    appearance: "promo"
  }), value.map((option, i) => /*#__PURE__*/React.createElement(MultiSelectChip, {
    key: option.value,
    ref: option.value ? chipRefs.current[option.value] : undefined,
    label: getOptionLabel(option),
    disabled: disabled
    // We need to render chips even if we show Badge, because we need to measure them.
    ,
    hidden: i > maxChipIndex && !isOpen || showBadgeForChips,
    size: size
    // eslint-disable-next-line react/jsx-no-bind -- Needed here to grab index
    ,
    onDelete: () => onDelete?.(i)
  }))) : null, hiddenChipCount > 0 && !isOpen && !showBadgeForChips && /*#__PURE__*/React.createElement("div", {
    className: cx("absolute w-6 py-1 text-left top-0", size === "sm" ? "bolt-font-body-xs-regular" : "bolt-font-body-s-regular", disabled ? "text-tertiary" : "text-primary"),
    style: {
      left: visibleChipOffset + chipGap
    }
  }, `+${hiddenChipCount}`), /*#__PURE__*/React.createElement("input", _extends({
    ref: inputRef,
    disabled: disabled,
    name: name,
    onChange: onInputChange,
    onFocus: openMenu,
    className: cx("block bg-special-nulled border-none outline-none p-0 flex-shrink-0 truncate", size === "sm" ? "bolt-font-body-s-regular" : "bolt-font-body-m-regular", disabled ? "cursor-not-allowed" : "cursor-text", size === "sm" ? "h-6" : "h-7", getInputTextStyles(disabled, !!inputValue.length), isValueArray && value.length > 0 && "placeholder:opacity-0"),
    style: isOpen ? {
      position: "absolute",
      left: isValueArray && value.length > 0 ? chipFullWidth + chipGap : 0,
      width: isValueArray && value.length > 0 ? inputWidthWhenOpen - chipGap : "100%"
    } : {
      position: "absolute",
      left: visibleChipOffset + (Array.isArray(value) && value.length > 0 ? chipGap : 0) + (hiddenChipCount > 0 ? CHIPS_COUNT_SAFEAREA + chipGap : 0),
      width: isValueArray && value.length > 0 ? inputWidthWhenClosed - chipGap : "100%"
    },
    placeholder: placeholder,
    type: "text",
    "aria-required": required
  }, inputA11yProps)))), renderEndSlot());
}